<template>
    <div
        class="gallery-layout-module gallery-layout-module--slider"
        :class="[mockupTypeClass, controlThemeClass]"
        :style="sliderVars"
    >
        <div class="gallery-slider__stage" :style="stageStyle">
            <div ref="emblaViewport" class="gallery-slider__viewport">
                <div class="gallery-slider__track">
                    <div
                        v-for="(item, index) in items"
                        :key="item.media?.url || index"
                        class="gallery-slider__slide"
                    >
                        <slot
                            name="gallery-item"
                            :item="item"
                            :index="index"
                            :scroll-hint-priority="index === selectedScrollSnap ? 0 : index + 1"
                        />
                    </div>
                </div>
            </div>
            <GalleryArrowButtons
                v-if="section?.controlsPosition === C.SIDES"
                :control-position="section?.controlsPosition"
                @next="scrollNext()"
                @previous="scrollPrevious()"
            />
        </div>

        <slot
            v-if="section.showCaptions"
            name="caption"
            :active-item="activeGalleryItem"
            :active-index="selectedScrollSnap"
            :align-left="false"
        />

        <GalleryControls
            :items-length="items.length"
            :scroll-snaps="scrollSnaps"
            :selected-scroll-snap="selectedScrollSnap"
            :controls-position="section?.controlsPosition"
            :layout-type="section?.layout"
            @previous="scrollPrevious"
            @next="scrollNext"
            @scroll-to="scrollTo"
        />
    </div>
</template>

<script>
import EmblaCarousel from "embla-carousel";
import GalleryControls from "@/scripts/sections/modules/gallery/GalleryControls.vue";
import {GalleryControlsPosition} from "@/scripts/common/enums.js";
import GalleryArrowButtons from "@/scripts/sections/modules/gallery/GalleryArrowButtons.vue";
import {mapGetters} from "vuex";
import {resolveColor} from "@/scripts/common/helpers.js";
import {getControlThemeClass} from "@/scripts/common/utilities/colorUtils.js";

export default {
    name: "GallerySlider",
    components: {
        GalleryArrowButtons,
        GalleryControls
    },
    props: {
        section: {
            type: Object,
            default: null
        },
        items: {
            type: Array,
            default: () => []
        },
        pendingFocusIndex: {
            type: Number,
            default: null
        },
        isPreview: {
            type: Boolean,
            default: false
        }
    },
    emits: ['gallery-item-focus-applied'],
    data() {
        return {
            emblaApi: null,
            selectedScrollSnap: 0,
            scrollSnaps: [],
            C: GalleryControlsPosition
        }
    },
    mounted() {
        this.initSlider();
    },
    beforeUnmount() {
        this.destroySlider();
    },
    watch: {
        items() {
            this.reInitSlider();
            this.focusPendingItem();
        },
        pendingFocusIndex() {
            this.focusPendingItem();
        }
    },
    computed: {
        ...mapGetters("design", ["colorPalette", "resolveColor"]),
        mockupTypeClass() {
            return this.items[0]?.media?.mockupSettings?.deviceType.toLowerCase() || "no-mockup";
        },
        controlThemeClass() {
            return getControlThemeClass(
                this.section?.backgroundSettings?.backgroundColor || this.resolveColor("background"),
                this.colorPalette
            );
        },
        stageStyle() {
            const style = {
                "--gallery-viewport-radius": `${this.section?.cornerRadiusPx ?? 0}px`
            };
            const stageBackgroundColor = this.section?.stageBackgroundColor;
            if (!stageBackgroundColor || this.mockupTypeClass === "no-mockup") return style;

            return {
                ...style,
                backgroundColor: resolveColor(stageBackgroundColor, this.colorPalette)
            };
        },
        sliderVars() {
            return this.mockupTypeClass !== 'no-mockup' ? null : {
                '--gallery-slider-cols': this.noMockupColumnSpan,
                '--gallery-slider-start': this.noMockupColumnStart,
                '--gallery-slider-aspect-ratio': this.normalizedAspectRatio
            };
        },
        noMockupColumnSpan() {
            const ratioColumns = {
                "16_9": 8,
                "4_3": 8,
                "1_1": 6,
                "3_4": 4,
                "9_16": 4
            };
            return ratioColumns[this.section?.mediaAspectRatio] || 8;
        },
        noMockupColumnStart() {
            return Math.floor((12 - this.noMockupColumnSpan) / 2) + 1;
        },
        normalizedAspectRatio() {
            const ratio = this.section?.mediaAspectRatio || '16_9';
            return ratio.replace('_', ' / ');
        },
        activeGalleryItem() {
            return this.items[this.selectedScrollSnap] || null;
        }
    },
    methods: {
        initSlider() {
            if (!this.$refs.emblaViewport || !this.items.length) return;
            this.emblaApi = EmblaCarousel(this.$refs.emblaViewport, {
                align: "center",
                containScroll: "trimSnaps",
                loop: true,
                slidesToScroll: 1
            });
            this.emblaApi.on("select", this.updateSliderState);
            this.emblaApi.on("reInit", this.updateSliderState);
            this.updateSliderState();
            this.focusPendingItem();
        },
        destroySlider() {
            this.emblaApi?.destroy();
            this.emblaApi = null;
        },
        reInitSlider() {
            this.$nextTick(() => {
                if (!this.emblaApi) {
                    this.initSlider();
                    return;
                }
                this.emblaApi.reInit();
                this.focusPendingItem();
            });
        },
        focusPendingItem() {
            const targetIndex = this.pendingFocusIndex;
            if (!Number.isInteger(targetIndex)) return;
            if (targetIndex < 0 || targetIndex >= this.items.length) return;

            this.$nextTick(() => {
                if (!this.emblaApi) return;
                this.emblaApi.scrollTo(targetIndex);
                this.$emit('gallery-item-focus-applied', targetIndex);
            });
        },
        updateSliderState() {
            if (!this.emblaApi) return;
            this.selectedScrollSnap = this.emblaApi.selectedScrollSnap();
            this.scrollSnaps = this.emblaApi.scrollSnapList();
        },
        scrollPrevious() {
            this.emblaApi?.scrollPrev();
        },
        scrollNext() {
            this.emblaApi?.scrollNext();
        },
        scrollTo(index) {
            this.emblaApi?.scrollTo(index);
        }
    }
}
</script>

<style scoped lang="scss">
@import "@/styles/components/sections/modules/gallery_slider.scss";
</style>
